// import AddIcon from "@mui/icons-material/Add";
// import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
// import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
// import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
// import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
// import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
// import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import {
  Avatar,
  Badge,
  Box,
  ButtonBase,
  CircularProgress,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import Flex from "../../../../../../../components/Flex";
import Text from "../../../../../../../components/Text";
import { VisuallyHiddenInput } from "../../../../../../../components/VisuallyHiddenInput";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
  PatientInfoBadge,
} from "../../../../../../../components/app/provider/patient/chart";
import {
  BirthdayCakeIcon,
  // PatientAddressIcon,
  PatientCalendarIcon,
  PatientEmailIcon,
  PatientFlagIcon,
  PatientGlobeIcon,
  PatientIDCardIcon,
  PatientLanguageIcon,
  PatientPhoneIcon,
  PatientStethoscopeIcon,
} from "../../../../../../../components/svg-icons/PatientSidebarIcons";
import { useRoleController } from "../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useGetPatientFlagList } from "../../../../../../../queries/flags/patientFlags/use-get-patient-flag-list";
import { PRIMITIVE } from "../../../../../../../tokens/design-tokens";
import {
  formatDateTime,
  getFormattedDateWithoutTmz,
} from "../../../../../../../utils/date-time-utility";
import {
  DEFAULT_ICON_COLOR,
  DEF_PAGINATION,
  convertToBase64,
  getAge,
  getValueFromObj,
  getXtenantId,
} from "../../../../../../../utils/utils";
import SendBulkEmailModal from "../../../../communications/email/SendBulkEmailModal";
import { PATIENT_CONTACT_ERROR } from "./patient-side-bar-constant";
import { ContactDialModal } from "../../../../dashboard/twilio/ContactDial";
import { useChangePatientAvatar } from "../../../avatar/use-patient-change-avatar";
import UpsertPatientFormDrawer from "../demographics/UpsertPatientFormDrawer";
import UpsertPatientFlagDrawer from "./PatientFlags";
import FlagPopUp from "./flag/FlagPopUp";

interface Address {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  country?: string;
  zipcode?: string;
}

interface Props {
  data?: {
    ecarehealthPatientId?: string;
    uuid?: string;
    firstName?: string;
    lastName?: string;
    gender?: string;
    mrn?: string;
    birthDate?: string;
    mobileNumber?: string;
    email?: string | null;
    avatar?: string | null;
    timezone?: string;
    address?: Address;
    providerId?: Record<string, string>;
    lastAppointment?: string | null;
    nextAppointment?: string | null;
    language?: string;
    languages?: Record<string, string>;
    allergies?: string[];
    flag?: string[];
    riskScore?: number;
    callConsent?: boolean;
    emailConsent?: boolean;
  };
  tags?: string[];
}

const PatientInfoSection: React.FC<Props> = ({ data }) => {
  const { data: flagList, refetch: refetchPatientFlags } =
    useGetPatientFlagList({
      patientUuid: data?.uuid,
      pagination: DEF_PAGINATION,
      xTENANTID: getXtenantId(),
      archive: false,
      gridSorting: [],
      enabled: !!data?.uuid,
    });

  const transformFlagList = (flagList: any[]) => {
    const transformedFlags: Array<{
      value: string;
      label: string;
      color: string;
    }> = [];

    flagList?.forEach((flagItem) => {
      const flags = flagItem.flags || {};
      const color = flagItem.flagColour || PRIMITIVE.color.neutrals[2700];

      // Extract key-value pairs from flags object
      Object.entries(flags).forEach(([key, value]) => {
        transformedFlags.push({
          value: key,
          label: value as string,
          color: color,
        });
      });
    });

    return transformedFlags;
  };

  const [flag, setFlag] = useState<
    Array<{ value: string; label: string; color: string }>
  >([]);
  const demographicsDisclosure = useDisclosure();
  const { mutateAsync: changePatientAvatar } = useChangePatientAvatar();
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);

  const handleChangeAvatar = async (base64File: string) => {
    setIsLoading(true);
    changePatientAvatar({
      changeAvatarRequest: {
        newAvatar: base64File,
        userId: data?.uuid,
      },
      xTENANTID: getXtenantId(),
    })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["patient", data?.uuid] });
        setIsLoading(false);
      })
      .finally(() => setIsLoading(false));
  };

  // Update flag state when API data changes
  useEffect(() => {
    if (flagList?.data?.content) {
      // transformFlagList returns [] for an empty list, so this also resets.
      setFlag(transformFlagList(flagList.data.content));
    }
  }, [flagList?.data?.content]);

  const disclosureHost = useDisclosure();
  const sendMailDisclosure = useDisclosure();
  const contactDialDisclosure = useDisclosure();
  const showSnackbar = useSnackbar();

  const handleCallPatient = () => {
    const errorMessage = !data?.mobileNumber
      ? PATIENT_CONTACT_ERROR.NO_PHONE_NUMBER
      : !data?.callConsent
        ? PATIENT_CONTACT_ERROR.NO_CALL_CONSENT
        : null;
    if (errorMessage) {
      showSnackbar({ type: "error", message: errorMessage });
      return;
    }
    contactDialDisclosure.open(data?.uuid);
  };

  const handleEmailPatient = () => {
    const errorMessage = !data?.email
      ? PATIENT_CONTACT_ERROR.NO_EMAIL_ADDRESS
      : !data?.emailConsent
        ? PATIENT_CONTACT_ERROR.NO_EMAIL_CONSENT
        : null;
    if (errorMessage) {
      showSnackbar({ type: "error", message: errorMessage });
      return;
    }
    sendMailDisclosure.open([
      {
        uuid: data?.uuid ?? "",
        name: `${data?.firstName} ${data?.lastName}`,
      },
    ]);
  };
  const age = data?.birthDate ? getAge(data?.birthDate) : "";
  const languageLabel = data?.languages
    ? Object.values(data?.languages).slice(0, 2).join(", ")
    : "-";
  const { hasUpdate: hasUpdatePatientCRUD } = useRoleController("Patient CRUD");

  // const addressLine = data?.address
  //   ? [
  //       data.address.line1,
  //       data.address.line2,
  //       data.address.city,
  //       data.address.state,
  //       data.address.zipcode,
  //     ]
  //       .filter(Boolean)
  //       .join(", ")
  //   : "";

  const nextApptDate = data?.nextAppointment
    ? new Date(data.nextAppointment)
    : null;

  const infoTextSx = {
    ...CHART_TYPOGRAPHY.body5,
    fontFamily: CHART_TYPOGRAPHY.fontFamily,
    color: CHART_COLORS.textDark,
  };

  const infoLabelSx = {
    ...infoTextSx,
    fontWeight: CHART_TYPOGRAPHY.weight.medium,
  };

  return (
    <Box width="100%">
      {/* ─── Patient Tag ─── */}
      <Box
        //  bgcolor={CHART_COLORS.bgWarning}
        overflow="hidden"
        p="12px"
      >
        <Flex alignItems="flex-start" position="relative">
          <Flex gap="14px" alignItems="center" flex={1}>
            <ButtonBase component="label" tabIndex={-1}>
              <VisuallyHiddenInput
                type="file"
                accept=".jpg,.jpeg,.png,.svg"
                onChange={async (event) => {
                  const files = event?.target?.files;
                  if (files) {
                    const base64File = await convertToBase64(files[0]);
                    handleChangeAvatar(base64File);
                  }
                }}
              />
              {isLoading ? (
                <CircularProgress size={48} color="inherit" />
              ) : (
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  variant="dot"
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: CHART_COLORS.statusLive,
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      height: CHART_DIMENSIONS.avatarSize,
                      width: CHART_DIMENSIONS.avatarSize,
                      bgcolor: CHART_COLORS.surfaceHeader,
                      color: CHART_COLORS.textDark,
                      fontSize: CHART_DIMENSIONS.avatarFontSize,
                      cursor: "pointer",
                    }}
                    alt="image"
                    src={data?.avatar ?? undefined}
                  >
                    {data?.firstName?.[0]}
                    {data?.lastName?.[0]}
                  </Avatar>
                </Badge>
              )}
            </ButtonBase>

            <Flex flexDirection="column" gap="4px" flex={1}>
              {/* Name + Age */}
              <Flex alignItems="baseline" gap="8px">
                <Text
                  sx={{
                    ...CHART_TYPOGRAPHY.body3,
                    fontFamily: CHART_TYPOGRAPHY.fontFamily,
                    fontWeight: CHART_TYPOGRAPHY.weight.medium,
                    color: CHART_COLORS.textDark,
                    wordBreak: "break-word",
                    cursor: hasUpdatePatientCRUD ? "pointer" : "default",
                  }}
                  onClick={() => {
                    if (hasUpdatePatientCRUD) demographicsDisclosure.open();
                  }}
                >
                  {data?.firstName} {data?.lastName}
                </Text>
                <Text
                  sx={{
                    ...CHART_TYPOGRAPHY.body5,
                    fontFamily: CHART_TYPOGRAPHY.fontFamily,
                    fontWeight: CHART_TYPOGRAPHY.weight.light,
                    color: CHART_COLORS.textDark,
                    whiteSpace: "nowrap",
                  }}
                >
                  {age}({data?.gender?.[0]?.toUpperCase() || "-"})
                </Text>
              </Flex>
              {/* ID + DOB row */}
              <Flex alignItems="center" gap="8px">
                <Flex alignItems="center" gap="4px">
                  <PatientIDCardIcon sx={{ color: CHART_COLORS.textDark }} />
                  <Text sx={infoTextSx}>
                    {data?.ecarehealthPatientId ?? "-"}
                  </Text>
                </Flex>
                <Flex alignItems="center" gap="4px">
                  <BirthdayCakeIcon sx={{ color: CHART_COLORS.textDark }} />
                  <Text sx={infoTextSx}>
                    {data?.birthDate
                      ? getFormattedDateWithoutTmz(data.birthDate)
                      : ""}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>

          {/* Right badges */}
          {/* <Flex
            flexDirection="column"
            alignItems="flex-end"
            gap="4px"
            position="absolute"
            top={0}
            right={0}
          >
            {(data?.allergies?.length ?? 0) > 0 && (
              <PatientInfoBadge
                variant="neutral"
                label={`${data?.allergies?.length} Allergies`}
              />
            )}
            {data?.riskScore != null && (
              <PatientInfoBadge
                variant="negative"
                label={`Risk ${data?.riskScore?.toFixed(2)}`}
              />
            )}
          </Flex> */}
        </Flex>
      </Box>

      {demographicsDisclosure.opened && (
        <UpsertPatientFormDrawer
          disclosureHost={demographicsDisclosure}
          patientData={data}
        />
      )}

      {/* ─── Separator ─── */}
      <Box height="1px" bgcolor={CHART_COLORS.border} mb={1} />

      {/* ─── Patient Info ─── */}
      <Box
      // bgcolor={CHART_COLORS.bgSecondaryYellow}
      >
        {/* Flags row */}
        <Flex alignItems="center" gap="8px" mb="12px" px="12px" my={1}>
          <Flex gap="4px" alignItems="center">
            <PatientFlagIcon />
            <Flex gap="6px" alignItems="center" flexWrap="nowrap">
              {flag.slice(0, 2).map((f) => (
                <PatientInfoBadge
                  key={f.value}
                  variant="warning"
                  label={f.label}
                  sx={{ maxWidth: "100px" }}
                />
              ))}
              {flag.length > 2 && (
                <FlagPopUp flags={flag} onEdit={() => disclosureHost.open()} />
              )}
            </Flex>
            <StyledButtonV2
              label="Add Flag"
              variant="text"
              shouldAddIcon
              onClick={() => disclosureHost.open()}
              sx={{
                fontFamily: CHART_TYPOGRAPHY.fontFamily,
              }}
            />
          </Flex>
        </Flex>

        {disclosureHost.opened && (
          <UpsertPatientFlagDrawer
            disclosureHost={disclosureHost}
            flag={flag}
            setFlag={setFlag}
            refetchPatientFlags={refetchPatientFlags}
          />
        )}

        {/* Contact info rows */}
        <Flex flexDirection="column" gap="2px" px="12px">
          {/* Phone | Timezone | Language */}
          <Flex alignItems="center" gap="8px" flexWrap="wrap" height={"22px"}>
            <Flex
              alignItems="center"
              gap="4px"
              onClick={handleCallPatient}
              sx={{ cursor: "pointer" }}
            >
              <PatientPhoneIcon />
              <Text sx={infoTextSx}>{data?.mobileNumber || "-"}</Text>
            </Flex>
            <Flex alignItems="center" gap="4px">
              <PatientGlobeIcon sx={{ color: DEFAULT_ICON_COLOR }} />
              <Text sx={infoTextSx}>{data?.timezone || "-"}</Text>
            </Flex>
            <Flex alignItems="center" gap="4px">
              <PatientLanguageIcon sx={{ color: DEFAULT_ICON_COLOR }} />
              <Text sx={infoTextSx}>{languageLabel || "-"}</Text>
            </Flex>
          </Flex>
          {/* Email */}
          <Flex alignItems="center" gap="4px" height={"22px"}>
            <PatientEmailIcon
              sx={{
                color: CHART_COLORS.primary,
                cursor: "pointer",
              }}
              onClick={handleEmailPatient}
            />
            <Text sx={infoTextSx}>{data?.email ?? "-"}</Text>
            <SendBulkEmailModal
              disClosureHost={sendMailDisclosure}
              disableAddPatient
            />
            {contactDialDisclosure.opened && (
              <ContactDialModal disclosureHost={contactDialDisclosure} />
            )}
          </Flex>
          {/* Address */}
          {/* commented -  ECRH-4255 */}
          {/* <Flex alignItems="flex-start" gap="4px">
            <PatientAddressIcon
              sx={{
                color: DEFAULT_ICON_COLOR,
                mt: "1px",
                flexShrink: 0,
              }}
            />
            <Text sx={{ ...infoTextSx, wordBreak: "break-word" }}>
              {addressLine || "-"}
            </Text>
          </Flex> */}
        </Flex>

        {/* ─── Separator ─── */}
        <Box height="1px" bgcolor={CHART_COLORS.border} my={1} />

        {/* Upcoming Appointment */}
        <Flex flexDirection="column" gap="4px" px="12px" mb={1}>
          <Text sx={infoLabelSx}>Upcoming Appointment:</Text>
          <Flex flexDirection="column" gap="2px">
            <Flex alignItems="center" justifyContent="space-between">
              <Flex alignItems="center" gap="4px">
                <PatientCalendarIcon />
                <Text sx={infoTextSx}>
                  {nextApptDate ? `${formatDateTime(nextApptDate)}` : "-"}
                </Text>
              </Flex>
              {nextApptDate && (
                <PatientInfoBadge
                  variant="confirmed"
                  label="Confirmed"
                  sx={{ borderRadius: "8px" }}
                />
                // <></>
              )}
            </Flex>
            <Text sx={infoTextSx}>
              <PatientStethoscopeIcon sx={{ mr: "4px" }} />
              {data?.providerId ? getValueFromObj(data.providerId) : "-"}
            </Text>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

export default PatientInfoSection;
